import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";

const inputCls = "focus-ring w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-teal-500";

export default function AdminLogin() {
  useSeo("Admin Login | RD Cleaning Services", "Admin login for RD Cleaning Services.");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("rd_admin_token", data.token);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-4">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl sm:p-10">
        <div className="flex flex-col items-center">
          <LogoMark size={48} />
          <h1 className="mt-4 font-display text-2xl font-extrabold tracking-tight text-navy">Admin Login</h1>
          <p className="mt-1 text-sm text-slate-500">RD Cleaning Services dashboard</p>
        </div>
        <form onSubmit={onSubmit} className="mt-8 space-y-5">
          {error && (
            <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" role="alert" data-testid="admin-login-error">{error}</p>
          )}
          <div>
            <label htmlFor="admin-email" className="mb-1.5 block text-sm font-semibold text-navy">Email</label>
            <input id="admin-email" data-testid="admin-email-input" type="email" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" placeholder="admin@example.com" />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-1.5 block text-sm font-semibold text-navy">Password</label>
            <input id="admin-password" data-testid="admin-password-input" type="password" required className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" placeholder="••••••••" />
          </div>
          <button type="submit" disabled={loading} className="focus-ring inline-flex w-full items-center justify-center gap-2 rounded-full bg-teal-600 px-6 py-3.5 text-sm font-bold text-white transition-all hover:bg-teal-700 disabled:opacity-60" data-testid="admin-login-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <Link to="/" className="focus-ring mt-6 block rounded text-center text-sm font-medium text-slate-500 hover:text-teal-700" data-testid="admin-back-home-link">
          ← Back to website
        </Link>
      </div>
    </div>
  );
}
